import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import useAuthStore from '../stores/authStore';

interface IotMessage {
  topic: string;
  message: string;
  timestamp: number;
}

interface UseWebSocketReturn {
  connected: boolean;
  messages: IotMessage[];
  subscribe: (topics: string[]) => void;
  unsubscribe: (topics: string[]) => void;
  publish: (topic: string, message: string) => void;
}

export function useWebSocket(userId?: string): UseWebSocketReturn {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState<IotMessage[]>([]);
  // 2026-09-14 修复（FE-53）：history 回放与实时 data 双通道去重
  const seenRef = useRef<Set<string>>(new Set());

  const mergeMessage = useCallback((msg: IotMessage) => {
    if (!msg || typeof msg.topic !== 'string') return;
    const key = `${msg.topic}|${msg.timestamp ?? ''}|${msg.message ?? ''}`;
    if (seenRef.current.has(key)) return;
    seenRef.current.add(key);
    if (seenRef.current.size > 300) {
      seenRef.current = new Set(Array.from(seenRef.current).slice(-150));
    }
    setMessages((prev) => [msg, ...prev].slice(0, 100));
  }, []);

  useEffect(() => {
    const token = useAuthStore.getState().token;
    const socket = io('/ws/iot', {
      transports: ['websocket'],
      // 2026-09-14 修复（FE-53）：携带 token（后端按租户回放/过滤），query.userId 保持兼容
      auth: token ? { token } : undefined,
      query: { userId, token },
    });

    socket.on('connect', () => {
      setConnected(true);
      // 2026-09-14 修复（FE-53）：连接即拉取历史回放——刷新页面后历史消息不丢（原实现仅内存态，刷新清零）
      socket.emit('history');
    });
    socket.on('disconnect', () => setConnected(false));

    socket.on('history', (hist: IotMessage[]) => {
      if (!Array.isArray(hist)) return;
      // 后端缓存按时间正序 → 反转合并（列表最新在前）
      [...hist].reverse().forEach((h) => mergeMessage(h));
    });

    socket.on('data', (data: IotMessage) => {
      mergeMessage(data);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userId, mergeMessage]);

  const subscribe = useCallback((topics: string[]) => {
    socketRef.current?.emit('subscribe', topics);
  }, []);

  const unsubscribe = useCallback((topics: string[]) => {
    socketRef.current?.emit('unsubscribe', topics);
  }, []);

  const publish = useCallback((topic: string, message: string) => {
    socketRef.current?.emit('publish', { topic, message });
  }, []);

  return { connected, messages, subscribe, unsubscribe, publish };
}
